import { supabase } from '@/integrations/supabase/client';
import { UserReview, UserReviewInsert, UserReviewUpdate, ReviewWithUser } from '@/contracts/userReviews';

/**
 * Data facade for user reviews
 * Handles CRUD operations and moderation
 */

// Get approved reviews for a product
export async function getProductReviews(productId: string): Promise<ReviewWithUser[]> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('product_id', productId)
    .eq('is_approved', true)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Fetch user profiles separately for each review
  const reviewsWithUsers = await Promise.all(
    (data || []).map(async (review) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, full_name, avatar_url')
        .eq('user_id', review.user_id)
        .single();

      return {
        ...review,
        user_email: profile?.email || null,
        user_full_name: profile?.full_name || null,
        user_avatar_url: profile?.avatar_url || null,
      };
    })
  );

  return reviewsWithUsers;
}

// Get user's own reviews (including unapproved)
export async function getUserReviews(userId: string): Promise<UserReview[]> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get single review by ID
export async function getReviewById(reviewId: string): Promise<ReviewWithUser | null> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('id', reviewId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Fetch user profile separately
  const { data: profile } = await supabase
    .from('profiles')
    .select('email, full_name, avatar_url')
    .eq('user_id', data.user_id)
    .single();

  return {
    ...data,
    user_email: profile?.email || null,
    user_full_name: profile?.full_name || null,
    user_avatar_url: profile?.avatar_url || null,
  };
}

// Check if user has reviewed a product
export async function hasUserReviewedProduct(userId: string, productId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('id')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
}

// Create a new review
export async function createReview(review: UserReviewInsert): Promise<UserReview> {
  const insertData = {
    product_id: review.product_id,
    user_id: review.user_id,
    rating: review.rating,
    title: review.title,
    content: review.content,
  };

  const { data, error } = await supabase
    .from('user_reviews')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update user's own review
export async function updateReview(reviewId: string, updates: UserReviewUpdate): Promise<UserReview> {
  const updateData: Record<string, any> = {};
  if (updates.rating !== undefined) updateData.rating = updates.rating;
  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.content !== undefined) updateData.content = updates.content;

  const { data, error } = await supabase
    .from('user_reviews')
    .update(updateData)
    .eq('id', reviewId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete review
export async function deleteReview(reviewId: string): Promise<void> {
  const { error } = await supabase
    .from('user_reviews')
    .delete()
    .eq('id', reviewId);

  if (error) throw error;
}

// Admin: Get all reviews (pending approval)
export async function getPendingReviews(): Promise<ReviewWithUser[]> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('is_approved', false)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Fetch user profiles separately for each review
  const reviewsWithUsers = await Promise.all(
    (data || []).map(async (review) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('email, full_name, avatar_url')
        .eq('user_id', review.user_id)
        .single();

      return {
        ...review,
        user_email: profile?.email || null,
        user_full_name: profile?.full_name || null,
        user_avatar_url: profile?.avatar_url || null,
      };
    })
  );

  return reviewsWithUsers;
}

// Admin: Approve review
export async function approveReview(reviewId: string, adminId: string): Promise<UserReview> {
  const { data, error } = await supabase
    .from('user_reviews')
    .update({
      is_approved: true,
      approved_by: adminId,
      approved_at: new Date().toISOString(),
    })
    .eq('id', reviewId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Admin: Reject review (delete)
export async function rejectReview(reviewId: string): Promise<void> {
  await deleteReview(reviewId);
}

// Get review statistics for a product
export async function getProductReviewStats(productId: string): Promise<{
  averageRating: number;
  totalReviews: number;
  ratingDistribution: Record<number, number>;
}> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('rating')
    .eq('product_id', productId)
    .eq('is_approved', true);

  if (error) throw error;

  const reviews = data || [];
  const totalReviews = reviews.length;

  if (totalReviews === 0) {
    return {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    };
  }

  const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
  const averageRating = sum / totalReviews;

  const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach(r => {
    ratingDistribution[r.rating as keyof typeof ratingDistribution]++;
  });

  return {
    averageRating: Math.round(averageRating * 10) / 10,
    totalReviews,
    ratingDistribution,
  };
}
